import { createBrowserAnalytics, observeAnalytics } from "@hereby/components/analytics";
import { createDialogStore } from "@hereby/components/dialog";
import { createIconPlugin } from "@hereby/components/icons";
import { setupTooltip } from "@hereby/components/tooltip";
import Alpine from "alpinejs";
import { moveChoiceFocus } from "./choice-keyboard";
import { createInquiryForm } from "./inquiry-form";
import { createRouterStore } from "./router";
import "./styles/main.css";

// 공용 다이얼로그 스토어 — 네이티브 alert() 대체. 랜딩은 컴포넌트 플러그인을
// 쓰지 않으므로(SDK·인증 없음) 여기서 직접 등록한다. 표시는 index.html 의
// ds-dialog.html include 가 담당.
Alpine.store("dialog", createDialogStore());
const landingRouter = createRouterStore();
Alpine.store("router", landingRouter);
const landingAnalytics = createBrowserAnalytics("landing", import.meta.env);

// 전역 아이콘 매직 `$icon` — 다이얼로그와 같은 사유로 여기서 직접 등록한다.
Alpine.plugin(createIconPlugin());

/**
 * 가격 섹션 — 단가·요금은 비공개(2026-07-14 방침), 인원수만 받아 초기 도입 상담으로 잇는다.
 * 실제 구간 단가는 영업 매뉴얼에서만 다룬다.
 */

/** 슬라이더 눈금 — HTML 의 min/max/step 도 이 값을 바인딩해서 한 곳에서만 정의한다. */
const SLIDER_MIN = 10;
const SLIDER_MAX = 500;
const SLIDER_STEP = 5;

/**
 * 반복 업무 시간 추정 계수 — 요금이 아니라 "지금 드는 시간"만 규모에 연동한다(단가 비공개 방침 유지).
 * 추정 기준은 카드 하단 각주로 그대로 공개한다.
 */
const STAFF_PER_WARD = 30; // TODO(영업 확인): 병동당 평균 인원 — 확정 전까지 각주에 기준 명시
const HOURS_PER_WARD = 4; // 근무표 작성 — 카드에 명시된 "병동당 매달 4시간"
const PAYROLL_HOURS = 24; // 급여 마감 "매달 3일" × 하루 8시간

Alpine.data("pricing", () => ({
  headcount: 120,
  sliderMin: SLIDER_MIN,
  sliderMax: SLIDER_MAX,
  sliderStep: SLIDER_STEP,

  /** 슬라이더 상한 = "500명 이상" — 대형 병원·체인은 견적이 아니라 별도 라이선스 상담 경로로 보낸다. */
  get isEnterprise(): boolean {
    return this.headcount >= this.sliderMax;
  },

  get headcountLabel(): string {
    return `${this.headcount}명${this.isEnterprise ? "+" : ""}`;
  },

  /** 병동 수 추정 — "지금 드는 시간" 앵커를 규모에 연동하기 위한 값. */
  get wardCount(): number {
    return Math.max(1, Math.round(this.headcount / STAFF_PER_WARD));
  },

  /** 매달 반복 업무 추정 시간 — 급여 마감 + 근무표만 합산(수시인 취합·보고는 제외한 보수적 추정). */
  get monthlyHours(): number {
    const raw = PAYROLL_HOURS + this.wardCount * HOURS_PER_WARD;
    return Math.round(raw / 10) * 10;
  },

  get quoteLabel(): string {
    return this.isEnterprise
      ? "대형 병원·체인 별도 라이선스 상담"
      : `직원 ${this.headcount}명 규모 초기 도입 상담`;
  },
}));

/**
 * 09 시작하기 — 도입 상담 신청 폼.
 *
 * 접수는 공개 API `inquiry.create` 가 받아 내부 영업 메일로 보낸다(저장 DB는 아직 없다).
 * 랜딩은 S3 정적 호스팅이라 서버가 없고, SDK 도 안 쓴다(인증이 없어 통째로 들일 이유가 없다)
 * — 그래서 tRPC 클라이언트 대신 fetch 로 직접 부른다. 봉투 형식은 postInquiry() 주석 참고.
 *
 * mailto 는 메일 앱이 없는 환경에서 아무 반응이 없어 접수 경로로 쓰지 않는다(2026-07-14).
 */

/** API 주소 — 미설정이면 로컬 API(:3000). 배포 환경은 .env 의 API_BASE_URL 이 채운다. */
const API_BASE_URL = import.meta.env.API_BASE_URL || "http://localhost:3000";

Alpine.data("contact", () => createInquiryForm(API_BASE_URL, landingAnalytics));

/**
 * 라이브 데모 존 — :4001 핵심 기능을 랜딩 안에서 재현한 실동작 시뮬레이션.
 * 로그인 없이 "AI가 일하는 순간"을 클릭 두 번 안에 보여주는 것이 목적.
 * (Phase 2: read-only 데모 계정 샌드박스 — 기획안 참고)
 */
interface PayRow {
  name: string;
  prev: number;
  cur: number;
  /**
   * 확인 대상 행의 편차 사유(툴팁). 실제 제품에서 편차는 늘 "어떤 수당·공제 항목이 얼마 움직였나"로
   * 환원된다(급여 레코드가 시트 항목에서 리니지를 물고 온다) — 그래서 사유도 항목 단위로 쓴다.
   * 기준이 5·10·20만 원이므로 걸릴 수 있는 행(박·정·송)에만 있으면 된다.
   */
  reason?: string;
}

interface AskItem {
  q: string;
  a: string;
  detail: string;
}

/**
 * 근무표 기준안: 4명 × 7일(월~일). 제약을 하나도 켜지 않았을 때의 "자유 배치"다.
 * 제약을 켤 때마다 buildDuty() 가 이 기준안을 고쳐 나간다 —
 * 그래야 체크를 켜고 끌 때 표가 실제로 달라지고, 체크박스의 의미가 화면으로 설명된다.
 */
const BASE_ROWS: { name: string; days: string[] }[] = [
  { name: "김○○", days: ["D", "E", "휴", "N", "N", "E", "D"] }, // 제약①: 야간 전담 아님
  { name: "이○○", days: ["D", "E", "D", "D", "E", "D", "E"] }, // 제약②: 목·금에도 근무
  { name: "박○○", days: ["E", "D", "E", "D", "E", "N", "휴"] },
  { name: "최○○", days: ["N", "N", "N", "E", "D", "휴", "E"] }, // 제약③: 야간 3연속
];

/** 근무 코드 → 사람이 읽는 이름 (툴팁용). D/E/N/휴 가 뭘 뜻하는지는 표만 봐서는 알 수 없다. */
function shiftLabel(shift: string): string {
  if (shift === "D") {
    return "주간 근무";
  }
  if (shift === "E") {
    return "저녁 근무";
  }
  if (shift === "N") {
    return "야간 근무";
  }
  return "휴무";
}

const DAY_LABEL = ["월", "화", "수", "목", "금", "토", "일"];

/** 칩이 순차로 채워지는 시간(4명 × 7일 × 45ms + 여유). 배지의 "n초"도 여기서 파생시켜 화면과 어긋나지 않게 한다. */
const DUTY_FILL_MS = BASE_ROWS.length * 7 * 45 + 350;

/** 켜진 제약을 지키도록 기준안을 고쳐 근무표를 만든다. */
function buildDuty(on: boolean[]): { name: string; days: string[] }[] {
  const rows = BASE_ROWS.map((r) => ({ name: r.name, days: [...r.days] }));
  if (on[0]) {
    // 김○○ 야간(N) 전담 — 주간·저녁을 빼고 N 과 휴만 남긴다
    rows[0].days = ["N", "N", "휴", "N", "N", "휴", "N"];
  }
  if (on[1]) {
    // 이○○ 목·금 연차 — 그 이틀은 반드시 휴
    rows[1].days[3] = "휴";
    rows[1].days[4] = "휴";
  }
  if (on[2]) {
    // 연속 나이트 2회 제한 — 최○○ 의 3연속 야간 중 세 번째를 저녁으로
    rows[3].days[2] = "E";
  }
  return rows;
}

/**
 * 편차 기준은 숫자가 아니라 "언제 이걸 고르는가"로 말한다 —
 * "± 5만 원"만으로는 그게 무슨 뜻인지, 고르면 내 일이 어떻게 달라지는지 안 읽힌다.
 * use = 상황, rule = 실제 기준(직원 1인의 전월 대비 금액 차이).
 */
interface PayThreshold {
  value: number;
  use: string;
  rule: string;
}

const PAY_THRESHOLDS: PayThreshold[] = [
  { value: 50_000, use: "수당·인사 변동이 있던 달", rule: "5만 원 이상 차이" },
  { value: 100_000, use: "평소 마감", rule: "10만 원 이상 차이" },
  { value: 200_000, use: "큰 변동만 빠르게", rule: "20만 원 이상 차이" },
];

const PAY_ROWS: PayRow[] = [
  { name: "김○○ · 간호팀", prev: 2_831_800, cur: 2_840_000 },
  {
    name: "박○○ · 병동A",
    prev: 2_186_000,
    cur: 2_310_000,
    // 질문 탭의 근태 답변(연장·야간 시간 증가)과 같은 사건을 가리킨다 — 두 탭이 한 이야기로 읽히게
    // \n = 툴팁 줄바꿈 (DS 툴팁의 nowrap 은 랜딩 main.css 에서 pre-line 으로 되살렸다)
    reason: "야간(N) 근무 3회 증가\n야간근로수당 +12.4만 원",
  },
  { name: "이○○ · 원무과", prev: 3_120_000, cur: 3_120_000 },
  {
    name: "정○○ · 물리치료",
    prev: 3_266_000,
    cur: 3_210_000,
    // 줄어든 것도 확인 대상 — 누락된 수당일 수 있어 실무에선 오히려 더 위험하다
    reason: "무급휴가 1일\n기본급 차감 −5.6만 원",
  },
  { name: "한○○ · 간호팀", prev: 2_412_000, cur: 2_430_000 },
  {
    name: "송○○ · 병동B",
    prev: 2_540_000,
    cur: 2_770_000,
    reason: "연장근무 14시간 + 당직 2회\n연장·당직수당 +23만 원",
  },
];

/**
 * 질문 데모는 경영·매니저 트랙 전용이다 — AI 질문 권한이 관리직에만 열리므로
 * 실무·운영 트랙에서는 탭 자체를 감춘다(demo.html 의 track 가드).
 * 그래서 트랙별 질문 세트가 아니라 경영용 한 벌만 둔다.
 * 톤: 원인 → 영향 → 판단 근거. "왜"에 답한다.
 */
const ASK_ITEMS: AskItem[] = [
  {
    // 급여 집계를 두 달 불러 비교한다. 지급·공제·실지급 합계와 상태별 건수가 실제 반환값이다.
    q: "이번 달 급여 총액이 지난달과 얼마나 다른가요?",
    a: "7월 실지급 총액은 3억 7,120만 원,\n전월 대비 +2.1%입니다.",
    detail:
      "지급 합계 4억 2,300만 원, 공제 합계 5,180만 원입니다.\n확정 251건 · 검토 대기 3건입니다.",
  },
  {
    // 직원 검색으로 대상을 찾고 연차 잔여를 조회한다. 부여·사용·잔여와 산정 내역이 실제 반환값이다.
    q: "김○○ 씨 남은 연차가 며칠인가요?",
    a: "김○○ 님의 잔여 연차는\n7.5일입니다.",
    detail:
      "올해 부여 15일 중 7.5일을 사용했습니다.\n어떻게 산정된 값인지 내역까지 함께 보여 드립니다.",
  },
  {
    // 근태 집계. 대상을 비우면 권한 범위 전체를 합산한다 — 정규·연장·야간·주말·휴일 시간과
    // 근무일수·지각·결근이 실제 반환값이다.
    q: "이번 달 연장근무가 얼마나 되나요?",
    a: "이번 달 연장근무는 412시간,\n야간은 168시간입니다.",
    detail:
      "근무일수 5,140일, 지각 6건 · 결근 1건입니다.\n권한 범위 안의 직원 254명을 합산한 값입니다.",
  },
];

/**
 * ④ 근로계약 진행 현황 (경영 트랙 전용) — 신규 입사자의 전자 근로계약이 어디까지 왔는지.
 * 상태 축은 실제 제품 전자서명 탭의 상태(작성 중 · 대기 중 · 체결)를 경영 언어로 옮긴 것:
 * 작성 중 → 서명 대기 → 완료. 계약서 열람·서명 화면 자체는 플랫폼이 아니라 hereby Sign
 * 쪽 화면이므로, 우측 문서 목업은 그 화면의 양식화된 축소 재현이다(스크린샷 아님).
 */
interface SignStage {
  key: string;
  /**
   * 상태 이름 — 트랙별로 갈린다. 경영은 "어디까지 왔나"의 현황 조망(작성 중·서명 대기·완료),
   * 실무는 "보내고 → 서명받고 → 끝"의 실행 흐름(전송됨·서명 대기·체결 완료).
   * 실제 제품 상태축(sent+writing → signer_waiting → completed/signed)을 두 관점으로 옮긴 것이라
   * 라벨만 다르고 같은 레코드를 가리킨다.
   */
  label: string;
  staffLabel: string;
  count: number;
  who: string;
  hint: string;
  staffHint: string;
  /** ds-label 색 모디파이어 — 회색(작성) → 주황(대기) → 초록(완료)으로 진행감을 준다 */
  badge: string;
  /**
   * 계약 조항 값 — 항목 구성은 4001 직원 추가(근로계약 섹션)의 실제 입력 필드를 따른다:
   * 계약 형태·급여 유형 / 기본급·식대·교통비 등 수당 / 주 소정근로시간. 부서·업무는 인사정보에서.
   * (임금 상세 = 기본급 + 수당 합이 wage 총액과 일치하도록 유지할 것)
   */
  doc: {
    employee: string;
    dept: string;
    duty: string;
    period: string;
    wage: string;
    wageDetail: string;
    /** true 면 임금 칸이 타이핑 애니메이션(signWageTyped)으로 그려진다 — 작성 중 상태 전용 */
    typing?: boolean;
    sentAt?: string;
    signedAt?: string;
  };
}

const SIGN_STAGES: SignStage[] = [
  {
    key: "writing",
    label: "작성 중",
    staffLabel: "전송됨",
    count: 1,
    who: "박○○ · 물리치료실",
    hint: "인사 담당자가 계약 조건을 입력하고 있습니다",
    staffHint: "전자계약을 보냈습니다 · 서명 요청 준비 중",
    badge: "ds-label--light-gray",
    doc: {
      employee: "박○○",
      dept: "물리치료실",
      duty: "물리치료 업무",
      period: "2026. 08. 01",
      wage: "월 3,120,000원",
      wageDetail: "기본급 2,800,000 · 식대 200,000 · 교통비 120,000",
      typing: true,
    },
  },
  {
    key: "waiting",
    label: "서명 대기",
    staffLabel: "서명 대기",
    count: 2,
    who: "김○○ · 간호팀 외 1명",
    hint: "입사자 휴대폰으로 서명 요청이 가 있습니다",
    staffHint: "재단 서명 완료 · 입사자 서명을 기다리는 중",
    badge: "ds-label--primary",
    doc: {
      employee: "김○○",
      dept: "간호팀 (2병동)",
      duty: "병동 간호 업무",
      period: "2026. 08. 01",
      wage: "월 2,860,000원",
      wageDetail: "기본급 2,600,000 · 식대 200,000 · 교통비 60,000",
      sentAt: "서명 요청 07. 14 10:20",
    },
  },
  {
    key: "done",
    label: "완료",
    staffLabel: "체결 완료",
    count: 1,
    who: "최○○ · 간호팀",
    hint: "서명이 끝난 계약서 원본이 자동 보관되었습니다",
    staffHint: "서명 완료 · 계약서 원본이 자동 보관되었습니다",
    badge: "ds-label--green",
    doc: {
      employee: "최○○",
      dept: "간호팀 (3병동)",
      duty: "병동 간호 업무",
      period: "2026. 08. 01",
      wage: "월 2,860,000원",
      wageDetail: "기본급 2,600,000 · 식대 200,000 · 교통비 60,000",
      sentAt: "서명 요청 07. 13 09:10",
      signedAt: "입사자 서명 07. 15 18:42",
    },
  },
];

/**
 * AI 답변 속 증감 수치에 방향색을 입힌다 — 상향(+) = red, 하향(−/-) = blue.
 * 답변은 x-text 로는 부분 색칠이 안 되므로 x-html 로 그린다.
 * 입력은 전부 이 파일 안의 상수(ASK_ITEMS)지만, x-html 에 넣는 문자열이므로
 * 이스케이프를 거친 뒤에만 태그를 씌운다 — 나중에 문구가 외부에서 오더라도 안전하게.
 */
function withDeltaColor(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

  // 부호 + 숫자(+단위) — 타이핑 중 잘린 상태("+2.")도 그대로 물린다
  return escaped.replace(
    /([+\-−])(\d[\d,.]*(?:%|만\s?원|원|시간|건|명|명분)?)/g,
    (_m, sign: string, num: string) =>
      `<span class="${sign === "+" ? "text-red-600" : "text-blue-600"}">${sign}${num}</span>`,
  );
}

Alpine.data("demo", () => ({
  moveChoiceFocus,
  // 질문 데모(24시간 직원)가 자비스 포지셔닝의 대표 증거물 — 첫 탭으로 승격 (2026-07-14 피드백)
  tab: "ask" as "ask" | "duty" | "pay" | "sign" | "sheet",

  // ── ② AI 근무표 스케줄러 ──
  dutyRows: buildDuty([true, true, true]),
  /**
   * who = 조건이 걸린 대상, tag = 조건 자체(DS 라벨로 표시),
   * hint = 끄면 근무표가 어떻게 달라지는지 — 체크박스의 뜻을 화면이 직접 설명한다.
   */
  constraints: [
    {
      who: "김○○",
      tag: "야간(N) 전담",
      tone: "ds-label--blue",
      hint: "끄면 주간·저녁도 섞여 나옵니다",
      on: true,
    },
    {
      who: "이○○",
      tag: "목·금 연차",
      tone: "ds-label--aqua",
      hint: "끄면 그날에도 근무가 잡힙니다",
      on: true,
    },
    {
      who: "전 직원",
      tag: "연속 나이트 2회 제한",
      tone: "ds-label--red",
      hint: "끄면 3연속 야간이 나옵니다",
      on: true,
    },
  ],
  /** 지금 표에 실제로 반영된 제약. 생성 전이면 null — 체크 상태와 구분해야 배지·강조가 표와 어긋나지 않는다 */
  appliedConstraints: null as boolean[] | null,
  dutyFilled: false,
  dutyDone: false,

  /** 생성 버튼: 아직 안 눌렀거나, 제약이 바뀌어 표가 낡았을 때만 누를 수 있다 */
  get canGenerate(): boolean {
    const applied = this.appliedConstraints;
    return applied === null || this.constraints.some((c, i) => c.on !== applied[i]);
  },

  generateDuty(): void {
    const on = this.constraints.map((c) => c.on);
    this.appliedConstraints = on;
    this.dutyRows = buildDuty(on);
    this.dutyFilled = false;
    this.dutyDone = false;
    // 다음 프레임에 다시 채워 넣어 칩 애니메이션을 재시작
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dutyFilled = true;
        setTimeout(() => {
          this.dutyDone = true;
        }, DUTY_FILL_MS);
      });
    });
  },

  /** 생성 전으로 되돌리기 — 표를 비우고 제약도 기본값으로 */
  resetDuty(): void {
    this.appliedConstraints = null;
    this.dutyFilled = false;
    this.dutyDone = false;
    for (const c of this.constraints) {
      c.on = true;
    }
    this.dutyRows = buildDuty([true, true, true]);
  },

  /**
   * 그리드에 뿌릴 셀을 한 줄로 편다(행마다 이름 1칸 + 근무 7칸).
   * 중첩 x-for 로 그리면 dutyRows 를 교체할 때 Alpine 이 셀을 뒤섞어 넣는다 —
   * 키가 고정된 평면 루프 하나로 그려야 재생성해도 자리가 어긋나지 않는다.
   */
  get dutyCells(): { id: string; r: number; d: number; name: string; shift: string }[] {
    const cells: { id: string; r: number; d: number; name: string; shift: string }[] = [];
    this.dutyRows.forEach((row, r) => {
      cells.push({ id: `name-${r}`, r, d: -1, name: row.name, shift: "" });
      row.days.forEach((shift, d) => {
        cells.push({ id: `${r}-${d}`, r, d, name: "", shift });
      });
    });
    return cells;
  },

  /** 배지 숫자는 "표에 반영된" 제약 수 — 체크만 바꾼 상태와 섞이면 표와 어긋난다 */
  get appliedCount(): number {
    return (this.appliedConstraints ?? []).filter(Boolean).length;
  },

  get genSeconds(): string {
    return (DUTY_FILL_MS / 1000).toFixed(1);
  },

  /** 제약이 반영된 셀 하이라이트: ① 김○○ 의 N ② 이○○ 의 목·금 연차 ③ 최○○ 의 야간 3연속 해소 지점 */
  isLockedCell(row: number, day: number): boolean {
    return this.lockedBy(row, day) >= 0;
  },

  /** 이 칸을 강제한 제약의 인덱스. 제약과 무관하게 배정된 칸이면 -1 */
  lockedBy(row: number, day: number): number {
    const applied = this.appliedConstraints;
    if (!this.dutyDone || applied === null) {
      return -1;
    }
    if (applied[0] && row === 0 && this.dutyRows[0].days[day] === "N") {
      return 0;
    }
    if (applied[1] && row === 1 && (day === 3 || day === 4)) {
      return 1;
    }
    if (applied[2] && row === 3 && day === 2) {
      return 2;
    }
    return -1;
  },

  /**
   * 칸 툴팁 — 제약이 강제한 칸이면 "어떤 제약 때문인지", 아니면 "AI 가 배정한 근무"임을 말한다.
   * 표가 왜 이렇게 짜였는지를 칸 단위로 설명하는 것이 이 데모의 핵심이라 칸마다 붙인다.
   */
  cellTip(row: number, day: number): string {
    if (!this.dutyDone) {
      return "";
    }
    const r = this.dutyRows[row];
    const shift = r.days[day];
    // \n = 툴팁 줄바꿈 (.ds-tooltip 은 랜딩에서 pre-line)
    const base = `${r.name} · ${DAY_LABEL[day]}요일\n${shiftLabel(shift)}(${shift})`;
    const i = this.lockedBy(row, day);
    if (i < 0) {
      return `${base}\n제약과 무관하게 AI가 배정했습니다.`;
    }
    const c = this.constraints[i];
    return `${base}\n제약 반영: ${c.who} — ${c.tag}`;
  },

  // ── ③ 급여 편차 검증 ──
  payRows: PAY_ROWS,
  thresholds: PAY_THRESHOLDS,
  threshold: 100_000,
  /** 트랙별 마지막 액션(경영=담당자 검토 요청 / 실무=급여 확정)을 눌렀는지 — 기준을 바꾸면 다시 false */
  actionDone: false,

  diff(row: PayRow): number {
    return row.cur - row.prev;
  },

  isFlagged(row: PayRow): boolean {
    return Math.abs(this.diff(row)) >= this.threshold;
  },

  /** 기준을 고르기 전에 "이걸 고르면 몇 건을 보게 되는지"를 칩에 미리 보여준다 */
  countFor(value: number): number {
    return this.payRows.filter((r) => Math.abs(this.diff(r)) >= value).length;
  },

  get flagCount(): number {
    return this.payRows.filter((r) => this.isFlagged(r)).length;
  },

  formatDiff(row: PayRow): string {
    const d = this.diff(row);
    if (d === 0) {
      return "±0";
    }
    const sign = d > 0 ? "+" : "−";
    return `${sign}${Math.abs(d).toLocaleString("ko-KR")}`;
  },

  // ── ① 24시간 직원에게 질문하기 ──
  askItems: ASK_ITEMS,
  askIndex: 0,
  askKey: 0,
  /** 답변 타이틀은 한 글자씩 타이핑된다. 하단 상세 카피는 타이핑이 끝난 뒤 한 번에 뜬다 */
  typed: "",
  typingDone: false,
  typeTimer: 0,

  /** 증감 수치에 방향색(+red / −blue)을 입힌 HTML — x-html 로 그린다 */
  get typedHtml(): string {
    return withDeltaColor(this.typed);
  },

  get detailHtml(): string {
    return withDeltaColor(this.askItems[this.askIndex].detail);
  },

  init(): void {
    this.typeAnswer();
    this.typeSignWage();
  },

  ask(i: number): void {
    this.askIndex = i;
    this.askKey += 1; // answer-card 애니메이션 재시작용
    this.typeAnswer();
  },

  typeAnswer(): void {
    window.clearInterval(this.typeTimer);
    const full = this.askItems[this.askIndex].a;

    // 모션 최소화 설정이면 타이핑 없이 즉시 완성 상태로
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.typed = full;
      this.typingDone = true;
      return;
    }

    this.typed = "";
    this.typingDone = false;
    let n = 0;
    this.typeTimer = window.setInterval(() => {
      n += 1;
      this.typed = full.slice(0, n);
      if (n >= full.length) {
        window.clearInterval(this.typeTimer);
        this.typingDone = true;
      }
    }, 32);
  },

  // ── ④ 근로계약 진행 현황 (경영 전용) ──
  signStages: SIGN_STAGES,
  /** 호버(터치는 탭)로 고른 상태 — 우측 계약서가 이 상태의 화면으로 바뀐다 */
  signIndex: 0,
  /**
   * 작성 중 상태의 임금 칸은 담당자가 입력하는 것처럼 금액 전체를 타이핑한다 —
   * 중간에 멈춘 채 두면("월 2,8") 입력 중이 아니라 값이 깨진 것처럼 읽힌다.
   */
  signWageTyped: "",
  signWageTimer: 0,

  get signStage(): SignStage {
    return this.signStages[this.signIndex];
  },

  /** 상태 선택 — 작성 중으로 (되)돌아올 때마다 임금 타이핑을 처음부터 다시 */
  pickSign(i: number): void {
    if (this.signIndex === i) {
      return;
    }
    this.signIndex = i;
    if (i === 0) {
      this.typeSignWage();
    }
  },

  typeSignWage(): void {
    window.clearInterval(this.signWageTimer);
    const full = this.signStages[0].doc.wage;

    // 모션 최소화 설정이면 타이핑 없이 완성 금액으로
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.signWageTyped = full;
      return;
    }

    this.signWageTyped = "";
    let n = 0;
    this.signWageTimer = window.setInterval(() => {
      n += 1;
      this.signWageTyped = full.slice(0, n);
      if (n >= full.length) {
        window.clearInterval(this.signWageTimer);
      }
    }, 110);
  },
}));

/**
 * 04 전체 기능 색인 — 13개를 다 펴면 이 섹션만 화면 세 개 분량이라 기본은 접어 둔다.
 * 접힌 높이와 아래쪽 페이드는 main.css 의 .feature-index 소관이고, 여기서는 상태만 든다.
 */

/** 펼침 애니메이션 길이 — main.css 의 .feature-index transition 과 같은 값이어야 한다. */
const FEATURE_INDEX_ANIM_MS = 500;
Alpine.data("featureIndex", () => ({
  expanded: false,

  toggle(): void {
    this.expanded = !this.expanded;

    // 접을 때는 목록 머리로 되돌려 준다 — 그러지 않으면 화면이 목록 아래(05 만든 사람들)로
    // 툭 떨어져, 접었는데 엉뚱한 섹션을 보고 있는 상태가 된다.
    if (!this.expanded) {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      document.getElementById("feature-index")?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
    }

    // 목록 높이가 변하면 [data-track-end] 문단의 위치도 같이 움직인다 —
    // 트랙 스위치는 그 문단을 기준으로 숨고 나타나므로(landing.init 의 onScroll),
    // 펼침이 끝난 뒤 한 번 다시 재게 해서 스위치가 낡은 상태로 남지 않게 한다.
    window.setTimeout(() => window.dispatchEvent(new Event("scroll")), FEATURE_INDEX_ANIM_MS);
  },
}));

/** 테마 저장 키 — index.html 의 부트 스크립트(첫 페인트 전 적용)와 같은 키를 쓴다. */
const THEME_KEY = "hereby-landing-theme";

Alpine.data("landing", () => ({
  moveChoiceFocus,
  mobileMenuOpen: false,
  scrolled: false,
  track: "owner" as "owner" | "staff",
  /** 다크 모드 여부 — 초기값은 부트 스크립트가 <html> 에 박아 둔 상태를 읽는다. */
  dark: document.documentElement.getAttribute("data-theme") === "dark",

  /** 푸터 해/달 토글 — <html data-theme> 전환 + 선택 저장(다음 방문에도 유지) */
  setTheme(dark: boolean): void {
    this.dark = dark;
    if (dark) {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    try {
      localStorage.setItem(THEME_KEY, dark ? "dark" : "light");
    } catch {
      // 저장소 접근 불가 환경(시크릿 모드 등) — 이번 세션만 적용되고 저장은 건너뛴다
    }
  },
  // 트랙 스위치 노출 여부. 트랙으로 갈리는 구간은 히어로~04 라
  // 04(#benefits)를 지나면 감춘다 — 눌러도 아무것도 안 바뀌는 컨트롤이
  // 계속 떠 있으면 고장처럼 읽힌다.
  trackVisible: true,

  init() {
    // 알약이 놓이는 선 = sticky top(64+8) + 알약 높이(≈44).
    // 기준은 08 도입의 카드 묶음([data-track-end], process.html) — 09 시작하기 직전이다.
    // 섹션 박스가 아니라 안쪽 블록을 재는 이유 — 섹션은 아래 패딩(128px)까지 포함이라
    // 글이 다 지나간 뒤에도 한참 더 붙어 있는다.
    const switchLine = 120;
    const trackEnd = document.querySelector("[data-track-end]");

    const onScroll = () => {
      this.scrolled = window.scrollY > 24;
      this.trackVisible = trackEnd ? trackEnd.getBoundingClientRect().bottom > switchLine : true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  },
}));

// 스크롤 리빌: [data-reveal] 요소가 뷰포트에 들어오면 .is-visible 부여
function setupReveal(): void {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
  );
  for (const el of document.querySelectorAll("[data-reveal]")) {
    if (el.getBoundingClientRect().top >= window.innerHeight) {
      el.classList.add("reveal-ready");
      observer.observe(el);
    } else {
      el.classList.add("is-visible");
    }
  }
}

document.addEventListener("DOMContentLoaded", setupReveal);

// DS 싱글톤 hover 툴팁 — 앱 시작 시 한 번만. 대상은 data-ds-tip="top" + title 로 opt-in 한다.
setupTooltip();

Alpine.start();
observeAnalytics(Alpine, landingAnalytics, () => {
  const path = (Alpine.store("router") as typeof landingRouter).path;
  return {
    screen:
      path === "/"
        ? "home"
        : path === "/terms/privacy-policy"
          ? "privacy"
          : path === "/terms/terms-of-service"
            ? "terms"
            : undefined,
  };
});
